import React, { Component } from "react";
import InstagramAutoSetupStarter from "./InstagramAutoSetupStarter";
import InstagramAutoSetupResult from "./InstagramAutoSetupResult";

const AUTO_SETUP_ANALYSIS_RESULT_STORAGE_KEY =
  "instagram_auto_setup_analysis_result";
const AUTO_SETUP_PENDING_RESULT_ENTRY_KEY =
  "instagram_auto_setup_pending_result_entry";
const AUTO_SETUP_ANALYSIS_PROFILE_SEQ_STORAGE_KEY =
  "instagram_auto_setup_analysis_company_profile_seq";

const getStepFromLocation = () =>
  new URLSearchParams(window.location.search).get("step");

const replaceUrlStep = (step) => {
  const url = new URL(window.location.href);
  url.searchParams.set("step", String(step));
  window.history.replaceState(
    window.history.state,
    "",
    `${url.pathname}${url.search}${url.hash}`
  );
};

const clearStoredAnalysisResult = () => {
  try {
    sessionStorage.removeItem(AUTO_SETUP_ANALYSIS_RESULT_STORAGE_KEY);
    sessionStorage.removeItem(AUTO_SETUP_PENDING_RESULT_ENTRY_KEY);
    sessionStorage.removeItem(AUTO_SETUP_ANALYSIS_PROFILE_SEQ_STORAGE_KEY);
  } catch (error) {
    console.warn("[InstagramAutoSetupStarterPage] 분석 결과 초기화 실패", error);
  }
};

// Result 페이지와 동일한 검사 기준으로 오래된/실패한 결과 복원을 막는다.
const isValidAutoSetupAnalysisResult = (value) => {
  if (!value || value.status !== "success" || !value.analysis?.source_analysis) {
    return false;
  }
  const inputType = String(value.input_type || value.source?.input_type || "").toUpperCase();
  return !["LINK", "COMPANY_URL"].includes(inputType) ||
    value.source?.url_analysis_status === "SUCCESS";
};

const isSameCompanyProfileContext = (companyProfileSeq) => {
  try {
    const storedProfileSeq = sessionStorage.getItem(
      AUTO_SETUP_ANALYSIS_PROFILE_SEQ_STORAGE_KEY
    );
    // 예전 임시 결과처럼 프로필 정보가 없는 경우에는 다른 프로필에 섞어 쓰지 않는다.
    return storedProfileSeq === String(companyProfileSeq || "");
  } catch (error) {
    return false;
  }
};

const loadStoredAnalysisResult = () => {
  try {
    const raw = sessionStorage.getItem(
      AUTO_SETUP_ANALYSIS_RESULT_STORAGE_KEY
    );
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    console.warn(
      "[InstagramAutoSetupStarterPage] 분석 결과 복원 실패",
      error
    );
    return null;
  }
};

const getCompanyProfileSeqFromLocation = () => {
  const value = Number(
    new URLSearchParams(window.location.search).get(
      "company_profile_seq"
    ) || 0
  );
  return Number.isFinite(value) && value > 0 ? value : null;
};

/**
 * AI 자동화 마법사의 진입 분기 담당 컴포넌트입니다.
 *
 * Home 화면은 이 페이지로 이동만 하고, 실제로 입력 화면을 보여줄지
 * 분석 결과 화면을 보여줄지는 이 컴포넌트가 관리합니다.
 */
class InstagramAutoSetupStarterPage extends Component {
  constructor(props) {
    super(props);

    const companyProfileSeq =
      props.companyProfileSeq ||
      props.company_profile_seq ||
      getCompanyProfileSeqFromLocation();

    const step = getStepFromLocation();
    const canRestoreStoredResult =
      step === "2" && isSameCompanyProfileContext(companyProfileSeq);
    const restoredResult = canRestoreStoredResult
      ? loadStoredAnalysisResult()
      : null;
    const resultCandidate = step === "2"
      ? props.analysisResult || restoredResult
      : null;
    const analysisResult = isValidAutoSetupAnalysisResult(resultCandidate)
      ? resultCandidate
      : null;

    this.needsStepReset = (step === "2" && !analysisResult) ||
      (step !== null && step !== "1" && step !== "2");
    this.state = { companyProfileSeq, analysisResult };
  }

  componentDidMount() {
    if (this.needsStepReset) {
      clearStoredAnalysisResult();
      replaceUrlStep("1");
    }
  }

  handleAnalyzeComplete = (analysisResult, payload) => {
    replaceUrlStep("2");
    this.setState({ analysisResult });

    if (typeof this.props.onAnalyzeComplete === "function") {
      this.props.onAnalyzeComplete(analysisResult, payload);
    }
  };

  handlePreviewImageReady = (previewImage, analysisResult) => {
    this.setState({ analysisResult });

    if (typeof this.props.onPreviewImageReady === "function") {
      this.props.onPreviewImageReady(previewImage, analysisResult);
    }
  };

  handleReturnToStarter = () => {
    clearStoredAnalysisResult();
    replaceUrlStep("1");
    this.setState({ analysisResult: null });
  };

  render() {
    const { analysisResult } = this.state;
    const { companyProfileSeq } = this.state;

    return (
      <>
        <style>{`
          #common-page-wrapper.instagram-auto-setup-wrapper {
            display: flex;
            flex-direction: column;
            width: 100%;
            height: 100dvh;
            min-height: 0;
          }
        `}</style>
        <div
          id="common-page-wrapper"
          className="instagram-auto-setup-wrapper"
        >
          {analysisResult ? (
            <InstagramAutoSetupResult
              analysisResult={analysisResult}
              companyProfileSeq={companyProfileSeq}
              onPreviewImageReady={this.handlePreviewImageReady}
              onReturnToStarter={this.handleReturnToStarter}
              onAutomationComplete={this.props.onAutomationComplete}
              onCompanyProfileUpdateComplete={
                this.props.onCompanyProfileUpdateComplete
              }
            />
          ) : (
            <InstagramAutoSetupStarter
              companyProfileSeq={companyProfileSeq}
              onAnalyze={this.props.onAnalyze}
              onAnalyzeComplete={this.handleAnalyzeComplete}
              onLogin={this.props.onLogin}
              onFreeStart={this.props.onFreeStart}
            />
          )}
        </div>
      </>
    );
  }
}

export default InstagramAutoSetupStarterPage;
